/**
 * MetricsSummaryCards
 *
 * Four top-level summary stat cards: total operations, total processing time,
 * total tokens, overall success rate.
 */

import { useTranslation } from '@/hooks/useTranslation';
import type { MetricsReport } from '@/services/metricsAPI';

interface MetricsSummaryCardsProps {
  report: MetricsReport;
}

const formatSeconds = (seconds: number): string => {
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  const minutes = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${minutes}m ${secs.toFixed(0)}s`;
};

export function MetricsSummaryCards({ report }: MetricsSummaryCardsProps): JSX.Element {
  const { t } = useTranslation();

  const totalSamples = report.operation_stats.reduce((sum, op) => sum + op.sample_count, 0);
  const totalSuccess = report.operation_stats.reduce((sum, op) => sum + op.success_count, 0);
  const successRate = totalSamples > 0 ? (totalSuccess / totalSamples) * 100 : 0;

  const successRateColor =
    successRate >= 95 ? 'text-green-400' : successRate >= 85 ? 'text-yellow-400' : 'text-red-400';

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6 sm:mb-8">
      <div className="panel-glass-card">
        <div className="text-white/50 text-xs sm:text-sm mb-1">
          {t('admin.metrics.totalOperations')}
        </div>
        <div className="text-white text-xl sm:text-2xl font-bold">
          {report.total_operations.toLocaleString()}
        </div>
      </div>

      <div className="panel-glass-card">
        <div className="text-white/50 text-xs sm:text-sm mb-1">{t('admin.metrics.totalTime')}</div>
        <div className="text-white text-xl sm:text-2xl font-bold">
          {formatSeconds(report.total_processing_seconds)}
        </div>
      </div>

      <div className="panel-glass-card">
        <div className="text-white/50 text-xs sm:text-sm mb-1">
          {t('admin.metrics.totalTokens')}
        </div>
        <div className="text-white text-xl sm:text-2xl font-bold">
          {report.total_tokens_charged.toLocaleString()}
        </div>
      </div>

      <div className="panel-glass-card">
        <div className="text-white/50 text-xs sm:text-sm mb-1">
          {t('admin.metrics.overallSuccessRate')}
        </div>
        <div className={`text-xl sm:text-2xl font-bold ${successRateColor}`}>
          {successRate.toFixed(1)}%
        </div>
      </div>
    </div>
  );
}
